import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as CommentActions from './comment.actions';
import { CommentService } from '../../services/CommentService';
import { catchError, map, mergeMap, of } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable()
export class CommentEffects {
  load$;
  add$;
  update$;
  delete$;
  addSuccess$;
  updateSuccess$;
  deleteSuccess$;
  loadFailure$;

  constructor(private actions$: Actions, private service: CommentService, private snackBar: MatSnackBar) {
    this.load$ = createEffect(() =>
      this.actions$.pipe(
        ofType(CommentActions.loadCommentsByReflex),
        mergeMap(({ reflexNo, isAdmin }) => {
          const request$ = isAdmin
            ? this.service.getByReflexAdmin(reflexNo)
            : this.service.getByReflex(reflexNo);
          return request$.pipe(
            map((comments) => CommentActions.loadCommentsByReflexSuccess({ reflexNo, comments })),
            catchError((err) => of(CommentActions.loadCommentsByReflexFailure({ reflexNo, error: err?.message ?? 'Error' })))
          );
        })
      )
    );

    this.add$ = createEffect(() =>
      this.actions$.pipe(
        ofType(CommentActions.addComment),
        mergeMap(({ reflexNo, commentText, clientId }) =>
          this.service.add(reflexNo, commentText).pipe(
            map((comment) => CommentActions.addCommentSuccess({ reflexNo, comment, clientId })),
            catchError((err) => {
              this.snackBar.open('Kommentar konnte nicht hinzugefügt werden', 'Schließen', { duration: 2500 });
              return of(CommentActions.loadCommentsByReflexFailure({ reflexNo, error: err?.message ?? 'Error' }));
            })
          )
        )
      )
    );

    this.update$ = createEffect(() =>
      this.actions$.pipe(
        ofType(CommentActions.updateComment),
        mergeMap(({ comment }) =>
          this.service.update(comment).pipe(
            map(() => CommentActions.updateCommentSuccess({ comment })),
            catchError((err) => {
              this.snackBar.open('Kommentar konnte nicht aktualisiert werden', 'Schließen', { duration: 2500 });
              const reflexNo = comment.reflexOrderNo ?? '';
              return of(CommentActions.loadCommentsByReflexFailure({ reflexNo, error: err?.message ?? 'Error' }));
            })
          )
        )
      )
    );

    this.delete$ = createEffect(() =>
      this.actions$.pipe(
        ofType(CommentActions.deleteComment),
        mergeMap(({ reflexNo, id }) =>
          this.service.delete(id).pipe(
            map(() => CommentActions.deleteCommentSuccess({ reflexNo, id })),
            catchError((err) => {
              this.snackBar.open('Kommentar konnte nicht gelöscht werden', 'Schließen', { duration: 2500 });
              return of(
                CommentActions.loadCommentsByReflexFailure({ reflexNo, error: err?.message ?? 'Error' }),
                CommentActions.loadCommentsByReflex({ reflexNo })
              );
            })
          )
        )
      )
    );
    // Success/failure notifications (must be inside ctor to ensure DI is ready)
    this.addSuccess$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(CommentActions.addCommentSuccess),
          map(() => this.snackBar.open('Kommentar hinzugefügt', 'Schließen', { duration: 2000 }))
        ),
      { dispatch: false }
    );

    this.updateSuccess$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(CommentActions.updateCommentSuccess),
          map(() => this.snackBar.open('Kommentar aktualisiert', 'Schließen', { duration: 2000 }))
        ),
      { dispatch: false }
    );

    this.deleteSuccess$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(CommentActions.deleteCommentSuccess),
          map(() => this.snackBar.open('Kommentar gelöscht', 'Schließen', { duration: 2000 }))
        ),
      { dispatch: false }
    );

    this.loadFailure$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(CommentActions.loadCommentsByReflexFailure),
          map(() => this.snackBar.open('Kommentare konnten nicht geladen werden', 'Schließen', { duration: 2500 }))
        ),
      { dispatch: false }
    );
  }
}
